import { useState, useEffect } from 'react'
import { Menu, X, ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { DocsSidebar } from './DocsSidebar'
import { DocsSearch } from './DocsSearch'
import { DocsBreadcrumb } from './DocsBreadcrumb'
import type { DocsPageMeta } from '@/types/docs'

interface DocsLayoutProps {
  children: React.ReactNode
  currentPath: string
  basePath: string
  searchIndex: DocsPageMeta[]
  backLink?: {
    href: string
    label: string
  }
}

/**
 * Main layout component for documentation pages
 * Provides sidebar navigation, search, and responsive behavior
 * Replaces the main UserLayout sidebar when viewing docs
 */
export function DocsLayout({
  children,
  currentPath,
  basePath,
  searchIndex,
  backLink,
}: DocsLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // Scroll to top when navigating to a new page
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [currentPath])

  return (
    <div className="min-h-screen">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - fixed position, replaces main app sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-card shadow-lg transform transition-transform duration-200 ease-in-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Sidebar header - mobile only */}
        <div className="flex h-14 items-center justify-between border-b px-4 lg:hidden">
          <span className="font-semibold">Documentation</span>
          <button
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
            className="p-1 rounded-md hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Back link */}
        {backLink && (
          <div className="border-b px-4 py-3">
            <Link
              to={backLink.href}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {backLink.label}
            </Link>
          </div>
        )}

        {/* Search */}
        <div className="p-4 border-b">
          <DocsSearch searchIndex={searchIndex} basePath={basePath} />
        </div>

        {/* Navigation */}
        <DocsSidebar basePath={basePath} className="h-[calc(100%-8rem)] overflow-y-auto" />
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <header className="sticky top-0 z-30 flex h-14 items-center gap-4 border-b bg-card px-4 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            aria-label="Open sidebar"
            className="p-1 rounded-md hover:bg-muted"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="font-medium truncate">Documentation</span>
        </header>

        {/* Content area */}
        <main className="px-6 py-6 lg:px-10 lg:py-8 max-w-none">
          <DocsBreadcrumb currentPath={currentPath} basePath={basePath} />
          <div className="max-w-4xl">
            {children}
          </div>
        </main>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          aside {
            display: none !important;
          }
          header {
            display: none !important;
          }
          main {
            max-width: none !important;
            padding: 0 !important;
          }
          nav[aria-label="Breadcrumb"] {
            display: none !important;
          }
        }
      `}</style>
    </div>
  )
}
